import { apiClient } from '../config/config'
import { Config } from '../config/config'
import Cookies from 'js-cookie'
import { registerEvents } from '../lib/utils/register-events'
import {
  AppAsyncActions,
  AppStore,
  MessageSeverity,
  SignUpData,
} from './app.types'
import { SiteDto } from '@/config/api-generated/model'

export const asyncActions = (curState: () => AppStore): AppAsyncActions => ({
  run: async (url: string, force) => {
    const {
      setLoading,
      setMessage,
      addProcess,
      setSignupVisible,
      processes,
      auth: { user },
    } = curState()

    if (!user) {
      setMessage({
        message: 'pleaseSignIn',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
      setSignupVisible(true)
      return
    }

    const existingProcess = Object.values(processes ?? []).find(
      (pr) => pr.url === url,
    )

    if (existingProcess && !force) {
      setMessage(
        {
          message: existingProcess.isFinished
            ? 'alreadyAddedFinished'
            : 'alreadyAddedRunning',
          severity: MessageSeverity.WARNING,
          tranlateNS: 'crawler.error',
        },
        0,
      )
      return
    }

    setLoading(true)

    const { settings } = curState()

    try {
      const res = await apiClient.postInit({
        ...settings,
        url,
      })

      const { id } = res.data
      if (id) {
        const live = `${Config.SERVER}/live?id=${id}`
        const finished = `${Config.SERVER}/finished?id=${id}`

        addProcess({
          id,
          pages: null,
          url,
          settings,
          isFinished: false,
          workers: {
            live,
            finished,
          },
        })

        registerEvents(id)

        setLoading(false)

        return id
      }

      setLoading(false)
    } catch {
      setMessage({
        message: 'fetchFailed',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })

      setLoading(false)
    }
  },
  signIn: async (email, password, onSuccess) => {
    const { setMessage, setUser, setLoading } = curState()

    setLoading(true)

    if (!email || !password) {
      setMessage({
        message: 'invalidCredentials',
        tranlateNS: 'auth.error',
        severity: MessageSeverity.ERROR,
      })
      return
    }

    try {
      const { data } = await apiClient.postSignin({
        email,
        password,
      })

      if (data && data?.accessToken && data?.refreshToken) {
        const { data: response } = await apiClient.getMe({
          headers: {
            Authorization: `Bearer ${data.accessToken}`,
          },
        })

        setUser({
          ...data,
          ...response,
        })

        Cookies.set('access_token', data.accessToken)
        Cookies.set('refresh_token', data.refreshToken)

        setMessage({
          message: 'loginSuccess',
          tranlateNS: 'global.message',
          severity: MessageSeverity.SUCCESS,
        })

        await onSuccess?.()
      }
    } catch (err) {
      console.error(err)
      setMessage({
        message: 'invalidCredentials',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)
  },
  signUp: async (userDetails: SignUpData) => {
    const { setMessage, setUserVerifyEmail, setLoading } = curState()
    const { name, email, password, passwordConfirm } = userDetails
    setLoading(true)

    if (!email || email.length < 5 || email.indexOf('@') < 2) {
      setMessage({
        message: 'invalidEmail',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
      return
    }

    if (
      !password ||
      password.length < 6 ||
      (passwordConfirm && password.localeCompare(passwordConfirm) !== 0)
    ) {
      setMessage({
        message: 'invalidPassword',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
      return
    }

    try {
      const { data } = await apiClient.postSignup({
        email,
        name,
        password,
      })

      if (data && data?.expiresAt) {
        setUserVerifyEmail(email)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error(err)
      setMessage({
        message: err?.response?.data?.message || 'invalidRegData',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)
  },
  refreshTokens: async () => {
    const {
      auth: { user },
      setUser,
      setLoading,
    } = curState()
    setLoading(true)

    try {
      const { data } = await apiClient.getRefresh({
        headers: {
          Authorization: `Bearer ${user?.refreshToken}`,
        },
      })
      if (data && user && data.accessToken && data.refreshToken) {
        setUser({
          ...user,
          ...data,
        })
      }
    } catch (err) {
      console.error(err)
    }

    setLoading(false)
  },
  fetchUser: async () => {
    const { setUser, setLoading } = curState()

    setLoading(true)
    try {
      const { data } = await apiClient.getMe()
      setLoading(false)
      if (data) {
        setUser({
          ...data,
          accessToken: Cookies.get('access_token'),
        })
      }
    } catch {
      setLoading(false)
      Cookies.remove('access_token')
      Cookies.remove('refresh_token')
      setUser(null)
    }
  },
  logout: async (onSuccess) => {
    const { setMessage, setUser } = curState()

    try {
      await apiClient.getLogout()
      setUser(null)
      Cookies.remove('access_token')
      Cookies.remove('refresh_token')

      await onSuccess?.()
    } catch {
      setMessage({
        message: 'unableLogout',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
    }
  },
  abortProcess: async (processId: string) => {
    const { setLoading, setMessage, load } = curState()
    setLoading(true)
    try {
      const res = await apiClient.postAbortWithId(processId)
      if (res) {
        load()
      }
    } catch (err) {
      console.error(err)
      setMessage({
        message: 'unableRemoveSite',
        tranlateNS: 'crawler.error',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)
  },
  deleteProcess: async (processId: string) => {
    const { setLoading, setMessage, load } = curState()
    setLoading(true)
    try {
      const res = await apiClient.deleteDeleteWithId(processId)
      if (res) {
        load()
      }
    } catch (err) {
      console.error(err)
      setMessage({
        message: 'unableRemoveSite',
        tranlateNS: 'crawler.error',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)
  },
  download: async (id, format) => {
    const {
      setLoading,
      setMessage,
      auth: { user },
    } = curState()
    setLoading(true)
    try {
      window.open(
        `${Config.SERVER}/crawler/export/${format.toLowerCase()}/${id}?includeDomain=true&token=${user?.accessToken || ''}`,
        '_blank',
      )
    } catch (err) {
      console.error(err)
      setMessage({
        message: 'unableDownload',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)
  },
  getSite: async (id: string) => {
    const {
      setLoading,
      setMessage,
      auth: { user },
      addProcess,
      updateProcess,
      processes,
    } = curState()
    if (!user || !user.accessToken) {
      setMessage({
        message: 'pleaseSignIn',
        tranlateNS: 'global.error',
        severity: MessageSeverity.ERROR,
      })
      return null
    }

    const site: SiteDto | null = null
    setLoading(true)
    try {
      const { data: site } = await apiClient.getSiteWithId(id)
      if (site && site?.id) {
        const { id } = site

        if (processes?.[id]) {
          updateProcess(id ?? '', {
            ...site,
          })
        } else {
          addProcess({
            id,
            url: site.url,
            pages: site.pages ?? null,
            pageCount: site.pageCount,
            settings: site.settings,
            isFinished: !!site?.finished,
          })

          registerEvents(id)
        }
      }
    } catch (err) {
      console.error(err)
      setMessage({
        message: 'unableLoadSite',
        tranlateNS: 'crawler.error',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)

    return site
  },
  load: async () => {
    const {
      setLoading,
      setMessage,
      auth: { user },
      clearAllProcess,
      addProcess,
    } = curState()

    if (!user) {
      setMessage({
        message: 'pleaseSignIn',
        tranlateNS: 'globa.error',
        severity: MessageSeverity.ERROR,
      })
      return
    }

    setLoading(true)
    try {
      const { data } = await apiClient.getSites()
      if (data) {
        clearAllProcess()
        data?.forEach((site) => {
          addProcess({
            id: site.id,
            url: site.url,
            pages: site.pages ?? null,
            pageCount: site.pageCount,
            settings: site.settings,
            isFinished: !!site?.finished,
          })

          registerEvents(site.id)
        })
      }
    } catch (err) {
      console.error(err)
      setMessage({
        message: 'unableLoadSite',
        tranlateNS: 'crawler.error',
        severity: MessageSeverity.ERROR,
      })
    }

    setLoading(false)
  },
})
